<template>
    <!--
        /reg/join — 새 벤더코드 참여 요청 (#442). 미리 등록된 업체코드가 없는 업체가 요청을 남기면
        레벨 4 관리자가 승인하고, 승인 메일의 초대 링크로 /reg 신청을 진행한다.
        ⛔ /reg 에서 이 화면으로 오는 링크는 일부러 두지 않는다(2026-10-12) — 필요할 때 주소를 직접 안내하고,
           노출이 정해지면 그때 링크를 붙인다.
    -->
    <main class="d-flex flex-column justify-content-between bgray-100 mh-100 vh-100">
        <b-navbar class="py-3 px-4 px-md-5" toggleable="sm">
            <b-navbar-brand class="header-logo">
                <b-img class="w-100" :src="require('@shared/assets/img/common/QuantifiedESG_Logo_v3_Basic.png')" alt="QESG"/>
            </b-navbar-brand>
            <b-navbar-nav class="ml-auto">
                <dropdownLanguage :disabled="sending"/>
            </b-navbar-nav>
        </b-navbar>

        <b-container class="bgray-100 py-3 my-auto" fluid>
            <b-row align-h="center" class="mb-4">
                <b-col cols md="8" xl="6" class="text-center">
                    <h2 class="fw-600 mb-2">{{ $t('평가 참여 요청') }}</h2>
                    <p class="gray-600 mb-0">{{ $t('업체코드를 받지 못한 경우 아래 정보를 남겨주세요. 확인 후 입력하신 이메일로 결과를 안내드립니다.') }}</p>
                </b-col>
            </b-row>

            <b-row align-h="center">
                <b-col cols md="8" xl="6">
                    <b-card class="px-lg-3 px-lg-4 py-3 shadow-sm">
                        <!-- 접수 결과 -->
                        <div v-if="result" class="text-center py-4">
                            <p class="f-110 fw-600 mb-3">{{ $t(result.title) }}</p>
                            <p class="gray-700 mb-4">{{ $t(result.message) }}</p>
                            <b-btn v-if="result.inquiry" variant="warning" @click="openInquiry">{{ $t('문의하기') }}</b-btn>
                            <template v-else>
                                <p class="f-90 gray-600 mb-3">{{ $t('안내 메일이 보이지 않으면 스팸함을 확인해 주세요.') }}</p>
                                <b-btn variant="outline-secondary" :to="{ name: 'Login' }">{{ $t('로그인') }}</b-btn>
                            </template>
                        </div>

                        <b-form v-else novalidate @submit.prevent="submit">
                            <b-form-group :label="`${$t('프로젝트 코드')} *`" label-for="join-project-code"
                                          :state="fieldState('project_code')" :invalid-feedback="$t(invalid['project_code'] || '')"
                                          :description="$t('평가를 안내받은 프로젝트 코드를 입력해주세요.')">
                                <b-form-input id="join-project-code" v-model="form.project_code" maxlength="50" :disabled="sending"/>
                            </b-form-group>
                            <b-form-group :label="`${$t('기업명')} *`" label-for="join-company-name"
                                          :state="fieldState('company_name')" :invalid-feedback="$t(invalid['company_name'] || '')">
                                <b-form-input id="join-company-name" v-model="form.company_name" maxlength="150" :disabled="sending"/>
                            </b-form-group>
                            <b-row>
                                <b-col cols="12" md="7">
                                    <b-form-group :label="`${$t('사업자등록번호')} *`" label-for="join-business-no"
                                          :state="fieldState('business_no')" :invalid-feedback="$t(invalid['business_no'] || '')"
                                                  :description="$t('해외 기업은 현지 사업자 등록번호를 입력해주세요.')">
                                        <b-form-input id="join-business-no" v-model="form.business_no" maxlength="100" :disabled="sending"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="5">
                                    <b-form-group :label="$t('국가')" label-for="join-country">
                                        <b-form-input id="join-country" v-model="form.country" maxlength="70" :disabled="sending"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group :label="`${$t('담당자 이름')} *`" label-for="join-applicant-name"
                                          :state="fieldState('applicant_name')" :invalid-feedback="$t(invalid['applicant_name'] || '')">
                                        <b-form-input id="join-applicant-name" v-model="form.applicant_name" maxlength="100" :disabled="sending"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group :label="$t('연락처')" label-for="join-applicant-tel">
                                        <b-form-input id="join-applicant-tel" v-model="form.applicant_tel" maxlength="50" :disabled="sending"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-form-group :label="`${$t('이메일')} *`" label-for="join-applicant-email"
                                          :state="fieldState('applicant_email')" :invalid-feedback="$t(invalid['applicant_email'] || '')"
                                          :description="$t('결과 안내를 받을 이메일입니다.')">
                                <b-form-input id="join-applicant-email" v-model="form.applicant_email" type="email" maxlength="100"
                                              placeholder="name@example.com" :disabled="sending"/>
                            </b-form-group>
                            <b-form-group :label="$t('요청 사항')" label-for="join-memo">
                                <b-textarea id="join-memo" v-model="form.memo" rows="3" maxlength="2000" :disabled="sending"/>
                            </b-form-group>

                            <b-alert :show="!!error" variant="danger" class="mb-3">
                                {{ $t(error || '') }}
                                <b-link v-if="error_inquiry" class="ml-2" @click="openInquiry">{{ $t('문의하기') }}</b-link>
                            </b-alert>

                            <div class="d-flex justify-content-between">
                                <b-btn variant="outline-secondary" :to="{ name: 'Register' }" :disabled="sending">{{ $t('돌아가기') }}</b-btn>
                                <b-btn variant="blue" type="submit" :disabled="sending">
                                    <b-spinner v-if="sending" small class="mr-1"/>
                                    {{ $t('참여 요청하기') }}
                                </b-btn>
                            </div>
                        </b-form>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <RegisterInquiryModal/>
        <global-footer/>
    </main>
</template>

<script>
import GlobalFooter from '@shared/components/common/GlobalFooter.vue';
import {resolveLocale} from '@/i18n';
import dropdownLanguage from '@shared/components/utils/dropdown-language.vue';
import RegisterInquiryModal, {REGISTER_INQUIRY_MODAL_ID} from '@/components/auth/Register/_SubComponents/RegisterInquiryModal.vue';

const REQUIRED = ['project_code', 'company_name', 'business_no', 'applicant_name', 'applicant_email'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// 이미 요청이 있을 때(상태와 상관없이 하나) 상태별 안내
const EXISTING = {
    pending: {title: '이미 접수된 참여 요청이 있습니다.', message: '검토 후 요청 때 입력하신 이메일로 결과를 안내드립니다.'},
    approved: {title: '이미 승인된 참여 요청입니다.', message: '승인 메일의 링크로 평가를 신청해주세요. 메일을 받지 못했다면 QESG로 문의해주세요.', inquiry: true},
    rejected: {title: '반려된 참여 요청이 있습니다.', message: '다시 요청하시려면 QESG로 문의해주세요.', inquiry: true},
};

export default {
    name: 'RegisterJoinRequest',
    components: {GlobalFooter, dropdownLanguage, RegisterInquiryModal},
    data() {
        return {
            form: {
                project_code: '', company_name: '', business_no: '', country: '',
                applicant_name: '', applicant_tel: '', applicant_email: '', memo: '',
            },
            sending: false,
            error: null,
            error_inquiry: false,
            // 필드별 오류 문구 키 {project_code: '필수 항목을 모두 입력해주세요.', ...} — 제출을 눌렀을 때만 채운다
            invalid: {},
            result: null,
        };
    },
    created() {
        // /reg 와 같은 규칙: ?l= → 저장된 언어 → 기본 ('ch' 는 'ca', 모르는 값은 en)
        this.$i18n.locale = resolveLocale(this.$route.query.l, this.$i18n.availableLocales);
        document.documentElement.lang = this.$i18n.locale;
    },
    methods: {
        fieldState(key) {
            return this.invalid[key] ? false : null;
        },
        setError(message, inquiry = false) {
            this.error = message;
            this.error_inquiry = inquiry;
        },
        async submit() {
            if (this.sending) return;
            const body = Object.fromEntries(Object.entries(this.form).map(([k, v]) => [k, v.trim()]));
            const invalid = {};
            REQUIRED.forEach(k => { if (!body[k]) invalid[k] = '필수 항목을 모두 입력해주세요.'; });
            if (!invalid.applicant_email && !EMAIL_RE.test(body.applicant_email)) invalid.applicant_email = '이메일을 정확히 입력하여 주십시오.';
            this.invalid = invalid;
            const first = REQUIRED.find(k => invalid[k]);
            if (first) {
                this.setError(invalid[first]);
                // 휴대폰에서 상단 안내가 화면 밖일 수 있어 첫 오류 칸으로 옮긴다 (REQUIRED 순서 = 화면 순서, id = join-<키>)
                document.getElementById('join-' + first.replace(/_/g, '-'))?.focus();
                return;
            }
            this.setError(null);

            this.sending = true;
            try {
                await this.$axios.post('/register/join-request', {...body, lang: this.$i18n.locale});
                this.result = {
                    title: '참여 요청이 접수되었습니다.',
                    message: '검토 후 입력하신 이메일로 결과를 안내드립니다.',
                };
            } catch (e) {
                this.handleError(e);
            } finally {
                this.sending = false;
            }
        },
        handleError(e) {
            const {status, data} = e?.response || {};
            if (status === 409 && data?.message === 'Join_Request_Exists') {
                this.result = EXISTING[data.status] || EXISTING.pending;
            } else if (status === 409 && data?.message === 'Already_Registered_Vendor') {
                this.result = {
                    title: '이 프로젝트에 이미 등록된 업체입니다.',
                    message: '업체코드는 평가를 안내한 원청사 또는 QESG로 문의해주세요.',
                    inquiry: true,
                };
            } else if (status === 404) {
                this.setError('프로젝트 코드를 확인해주세요.');
            } else {
                this.setError('참여 요청에 실패했습니다. 잠시 후 다시 시도해주시거나, 문제가 계속되면 QESG로 문의해주세요.', true);
            }
        },
        openInquiry() {
            this.$bvModal.show(REGISTER_INQUIRY_MODAL_ID);
        },
    },
};
</script>
